/**
 * Thin fetch wrapper over the Worker's Hono API.
 *
 * Everything the SPA reads goes through here so error shape, JSON parsing, the
 * /api prefix and the "session expired" reaction stay in one place. TanStack
 * Query owns caching/retries.
 */

const BASE_URL = '/api'

/** Login screen path — kept in sync with the route in router.tsx. */
const LOGIN_PATH = '/giris'

export class ApiError extends Error {
  readonly status: number
  readonly body: unknown

  constructor(status: number, message: string, body?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  })

  const raw = await response.text()
  const body: unknown = raw ? safeParse(raw) : null

  if (!response.ok) {
    // Expired session: whichever screen it came from, we return to the login
    // screen from this one place. Except the `/auth/*` endpoints — the 401 of a
    // login attempt is shown on the form, and the 401 of `/auth/me` is the answer
    // the route guard expects.
    if (response.status === 401 && !path.startsWith('/auth/')) {
      redirectToLogin()
    }
    throw new ApiError(response.status, errorMessage(body, response.status), body)
  }

  return body as T
}

/**
 * Full-page redirect (not the router): everything in the cache is reset and
 * api.ts stays free of any dependency on the router/query modules.
 */
function redirectToLogin(): void {
  const { pathname, search } = window.location
  if (pathname === LOGIN_PATH) return
  window.location.replace(`${LOGIN_PATH}?redirect=${encodeURIComponent(pathname + search)}`)
}

function safeParse(raw: string): unknown {
  try {
    return JSON.parse(raw)
  } catch {
    return raw
  }
}

function errorMessage(body: unknown, status: number): string {
  if (body && typeof body === 'object' && 'error' in body && typeof body.error === 'string') {
    return body.error
  }
  return `İstek başarısız oldu (${status}).`
}

/* ── Response shapes ────────────────────────────────────────────────────────── */

export interface HealthResponse {
  ok: boolean
  /** Result of `select 1` through Drizzle — 1 when the database answered. */
  db: number | null
  error?: string
}

export const api = {
  health: () => apiFetch<HealthResponse>('/health'),
}

export const queryKeys = {
  health: ['health'] as const,
}
